import { Fetcher } from '@this/src/util';
/* eslint max-lines: 0 */
import React, { useEffect, useState, useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

import HotelList from '@this/domain/hotel/hotel_list';
import type FlightList from '@this/domain/flight/flight_list';
import type TransitList from '@this/domain/transit/transit_list';

import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import SelectListAreaSearchTemplate from './list_area_search/list_area_search.template';
import type { SelectListAreaProps, SelectListAreaState } from './list_area';
import HotelListAreaContent from './HotelListAreaContent';
import TransportListAreaContent from './TransportListAreaContent';

type Props = SelectListAreaProps &
  SelectListAreaState & {
    handleTransportSegmentClick: (i: number) => () => void;
    toggleShowSearchBox: () => void;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    direction?: string;
    handleFlightConditionSubmit: (
      filteredUpgradeSeat: boolean,
      carrierIds: string[],
      changeablePrices: string[],
      showConnectingAir: boolean
    ) => void;
    openFlightCondition: () => void;
    closeFlightCondition: () => void;
    isOpenFlightCondition: boolean;
  };
const ListAreaLayout: React.FC<Props> = ({
  query,
  store,
  repository,
  serviceId,

  icon,
  listWrapper,
  type,
  subtype,
  show,
  queryItem,
  resultItem,
  direction,

  transportSegment,
  errors,
  showSearchBox,

  handleTransportSegmentClick,
  toggleShowSearchBox,
  handleSubmit,
  handleFlightConditionSubmit,
  openFlightCondition,
  closeFlightCondition,
  isOpenFlightCondition,
  isPcLayout = false
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme, show, isPcLayout), [theme, show, isPcLayout]);

  const [me, setMe] = useState<User | null>(null);
  useEffect(() => {
    const abortController = new AbortController();

    Fetcher.get<UserJson>('/users.json', undefined, undefined, abortController.signal).then(
      response => {
        setMe(new User(response));
      },
      error => {
        if (error.name !== 'AbortError') {
          setMe(null);
        }
      }
    );

    return () => {
      abortController.abort();
    };
  }, []);

  const searchArea =
    store.result.type === 'separate' &&
    (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
    store.result.items.length > 0 &&
    query &&
    queryItem &&
    type !== 'flight' ? (
      <SelectListAreaSearchTemplate
        showSearchBox={showSearchBox}
        toggleShowSearchBox={toggleShowSearchBox}
        handleSubmit={handleSubmit}
        queryItem={queryItem}
        query={query}
        errors={errors}
      />
    ) : null;

  const listContent =
    type === 'hotel' && listWrapper instanceof HotelList ? (
      <HotelListAreaContent
        listWrapper={listWrapper}
        resultItem={resultItem}
        icon={icon}
        serviceId={serviceId}
        store={store}
        isMobile={!isPcLayout}
        isPcLayout={isPcLayout}
        me={me}
        repository={repository}
      />
    ) : (type === 'transport' || type === 'flight') && listWrapper ? (
      <TransportListAreaContent
        type={type}
        listWrapper={listWrapper as TransitList | FlightList}
        icon={icon}
        subtype={subtype}
        resultItem={resultItem}
        store={store}
        repository={repository}
        direction={direction}
        queryItem={queryItem}
        transportSegment={transportSegment}
        handleTransportSegmentClick={handleTransportSegmentClick}
        openFlightCondition={openFlightCondition}
        closeFlightCondition={closeFlightCondition}
        isOpenFlightCondition={isOpenFlightCondition}
        handleFlightConditionSubmit={handleFlightConditionSubmit}
        isPcLayout={isPcLayout}
      />
    ) : null;

  return (
    <div style={styles.listArea} className="select-list-area">
      {!isPcLayout && searchArea}
      {resultItem && resultItem.loading ? (
        <>
          <img className="select__loading" src="/images/loading.gif" width={50} height={50} alt="" />
          {type === 'transport' ? (
            <div className="select__loading-text">経路を検索中です</div>
          ) : type === 'flight' ? (
            <div className="select__loading-text">航空券を検索中です</div>
          ) : type === 'hotel' ? (
            <div className="select__loading-text">ホテルを検索中です</div>
          ) : null}
        </>
      ) : !listWrapper ? null : isPcLayout ? (
        <div style={styles.listContent}>
          {searchArea && <div style={styles.searchArea}>{searchArea}</div>}
          {listContent}
        </div>
      ) : (
        listContent
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, show: boolean, isPcLayout: boolean) => {
  const isEditPage = location.pathname.match(/trips\/\d+\/edit/);
  const backgroundColor = isEditPage ? '#fff' : theme.accentColorLight;

  if (!isPcLayout) {
    return {
      listArea: {
        flexGrow: 99999,
        background: backgroundColor,
        padding: '10px',
        position: 'relative' as const,
        display: show ? 'block' : ('none' as const)
      },
      searchArea: {},
      listContent: {}
    };
  }

  return {
    listArea: {
      flex: '1 1 auto',
      maxHeight: '100%',
      background: backgroundColor,
      padding: '24px',
      position: 'relative' as const,
      display: show ? 'flex' : ('none' as const),
      flexDirection: 'column' as const,
      minHeight: 0,
      boxSizing: 'border-box' as const
    },
    searchArea: {
      flexShrink: 0
    },
    listContent: {
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column' as const
    }
  };
};

export default ListAreaLayout;
